import { Alert, Button, FormError, QueryView } from "@simplicity/ui";
import { useState, type ReactNode } from "react";
import { AgentChangeBadge } from "./OnsiteBadges";
import { formatDateTime, shortDigest } from "./onsiteModel";
import { ReleaseDialog } from "./ReleaseDialog";
import {
  APPROVER_ONLY,
  currentSourceText,
  hiddenCandidatesText,
  platformLine,
  promoteBlocker,
  releaseActionText,
  releaseBuildLabel,
  releaseDoneText,
  rollbackBlocker,
  shortFingerprint,
  type ReleaseIntent,
} from "./releasesModel";
import type {
  AgentReleaseRecord,
  AgentReleases,
  CurrentRelease as CurrentReleaseData,
  ReleaseCandidate,
} from "./types";
import { useAgentReleases } from "./useOnsite";

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words text-foreground">{children}</dd>
    </div>
  );
}

function CurrentFacts({ current }: { current: CurrentReleaseData }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      <Fact label="Build">{releaseBuildLabel(current)}</Fact>
      <Fact label="Fingerprint">
        <span className="font-mono text-xs">{shortFingerprint(current.fingerprint)}</span>
      </Fact>
      <Fact label="Source">{currentSourceText(current)}</Fact>
      {current.record?.note && <Fact label="Note">{current.record.note}</Fact>}
      <Fact label="Image">
        <span className="break-all font-mono text-xs">{current.image}</span>
      </Fact>
    </dl>
  );
}

function CurrentRelease({
  releases,
  onRollback,
}: {
  releases: AgentReleases;
  onRollback: (target: AgentReleaseRecord) => void;
}) {
  const { current, rollback } = releases;
  const target = rollback.target;
  return (
    <section className="form-card space-y-3" aria-label="Current release">
      <h3 className="text-lg text-foreground">Current release</h3>
      {current ? (
        <CurrentFacts current={current} />
      ) : (
        <p className="text-sm text-destructive">
          No current release: nothing has been promoted and ONSITE_AGENT_IMAGE is unset, so setup
          cannot run.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          disabled={rollbackBlocker(releases) !== null}
          onClick={() => target && onRollback(target)}
        >
          Roll back to previous release
        </Button>
        <span className="text-xs text-muted-foreground">
          {target ? `Returns to ${releaseBuildLabel(target)}.` : rollback.refusal}
        </span>
      </div>
    </section>
  );
}

function CandidateRow({
  candidate,
  releases,
  onPromote,
}: {
  candidate: ReleaseCandidate;
  releases: AgentReleases;
  onPromote: (candidate: ReleaseCandidate) => void;
}) {
  const name = candidate.tag ?? shortDigest(candidate.digest);
  const behind = candidate.behindWorkspaces;
  return (
    <tr>
      <td className="font-mono text-xs">{name}</td>
      <td>{formatDateTime(candidate.builtAt)}</td>
      <td className="font-mono text-xs">{shortFingerprint(candidate.fingerprint)}</td>
      <td>
        <AgentChangeBadge change={candidate.agentChange} />
      </td>
      <td title={behind.join(", ") || undefined}>
        {behind.length} of {releases.workspaceCount}
      </td>
      <td>
        <div className="space-y-1">
          <Button
            size="sm"
            disabled={promoteBlocker(releases.canPromote, candidate) !== null}
            aria-label={`Promote ${name}`}
            onClick={() => onPromote(candidate)}
          >
            Promote
          </Button>
          {candidate.refusal && (
            <p className="max-w-80 text-xs text-muted-foreground">{candidate.refusal}</p>
          )}
        </div>
      </td>
    </tr>
  );
}

function CandidatesTable({
  releases,
  onPromote,
}: {
  releases: AgentReleases;
  onPromote: (candidate: ReleaseCandidate) => void;
}) {
  if (releases.candidatesNote) return <Alert variant="warning">{releases.candidatesNote}</Alert>;
  if (releases.candidates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No build is newer than the current release.</p>
    );
  }
  return (
    <div className="admin-table-container">
      <table className="admin-table" aria-label="Newer builds">
        <thead>
          <tr>
            <th>Build</th>
            <th>Built</th>
            <th>Fingerprint</th>
            <th>Agent</th>
            <th>Behind if promoted</th>
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {releases.candidates.map((candidate) => (
            <CandidateRow
              key={candidate.digest}
              candidate={candidate}
              releases={releases}
              onPromote={onPromote}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewerBuilds({
  releases,
  onPromote,
}: {
  releases: AgentReleases;
  onPromote: (candidate: ReleaseCandidate) => void;
}) {
  const hidden = hiddenCandidatesText(releases.hiddenCandidates);
  return (
    <div className="space-y-2">
      <h3 className="text-lg text-foreground">Newer builds</h3>
      <CandidatesTable releases={releases} onPromote={onPromote} />
      {hidden && <p className="text-xs text-muted-foreground">{hidden}</p>}
      <p className="text-xs text-muted-foreground">{platformLine(releases.platform)}</p>
    </div>
  );
}

function ReleaseHistory({ history }: { history: AgentReleaseRecord[] }) {
  if (history.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No release recorded yet. The first promote records ONSITE_AGENT_IMAGE as the bootstrap
        release, then the promoted build.
      </p>
    );
  }
  return (
    <div className="admin-table-container">
      <table className="admin-table" aria-label="Release history">
        <thead>
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>Build</th>
            <th>By</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {history.map((record) => (
            <tr key={record.id}>
              <td>{formatDateTime(record.promotedAt)}</td>
              <td>{releaseActionText(record.action)}</td>
              <td className="font-mono text-xs">{releaseBuildLabel(record)}</td>
              <td>{record.promotedBy}</td>
              <td className="col-muted">{record.note ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Releases({ releases }: { releases: AgentReleases }) {
  const [intent, setIntent] = useState<ReleaseIntent | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const finish = (record: AgentReleaseRecord) => {
    setIntent(null);
    setDone(releaseDoneText(record));
  };
  return (
    <div className="space-y-6">
      {!releases.canPromote && <Alert variant="info">{APPROVER_ONLY}</Alert>}
      {done && <Alert variant="success">{done}</Alert>}
      <CurrentRelease
        releases={releases}
        onRollback={(target) => setIntent({ kind: "rollback", target })}
      />
      <NewerBuilds
        releases={releases}
        onPromote={(candidate) => setIntent({ kind: "promote", candidate })}
      />
      <div className="space-y-2">
        <h3 className="text-lg text-foreground">Release history</h3>
        <ReleaseHistory history={releases.history} />
      </div>
      {intent && (
        <ReleaseDialog
          intent={intent}
          releases={releases}
          onClose={() => setIntent(null)}
          onDone={finish}
        />
      )}
    </div>
  );
}

/** The agent image setup installs, the builds that may replace it, and who released what. */
export function ReleasesSection() {
  const query = useAgentReleases();
  return (
    <section className="space-y-4" aria-label="Agent releases">
      <div className="space-y-1">
        <h2 className="text-xl text-foreground">Agent releases</h2>
        <p className="text-sm text-muted-foreground">
          Every merge that changes the agent builds an image, but building is not releasing: Set up
          agent and Run setup again install the current release. Promote moves it to a newer build;
          Roll back returns to the one before.
        </p>
      </div>
      <QueryView
        query={query}
        loading={<div className="empty-state">Loading releases...</div>}
        error={(err) => <FormError assertive>Failed to load releases: {err.message}</FormError>}
      >
        {(releases) => <Releases releases={releases} />}
      </QueryView>
    </section>
  );
}
